import useBoard from '@/hooks/use-board.js'
import { getNavigationConfig } from '@/utils/navigationData'
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { featureAccessService } from '@/services/auth/feature-access.service'
import { getEmbedStatus } from '@/hooks/use-embed-mode'
import { logger } from '@/utils/logger'

/**
 * Hook that encapsulates navigation menu logic with optimizations for faster route transitions
 */
export default function useNavigation() {
  const { boardId } = useBoard()
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const isNavigating = ref(false)
  const routeLoadingProgress = ref(0)

  // For the progress bar animation
  let progressInterval = null

  const features = computed(() => store.getters['accessScopes/features'])
  const { isEmbedded } = getEmbedStatus()

  const navigationConfig = computed(() =>
    getNavigationConfig({
      boardId: boardId.value,
      role: store.getters.role(boardId.value),
      route,
      isVerified: store.getters.user?.emailVerified,
      features: features.value,
      isEmbedded
    })
  )

  // Set up router hooks for the progress bar
  const startProgress = () => {
    routeLoadingProgress.value = 0
    isNavigating.value = true

    // Clear any existing interval
    if (progressInterval) clearInterval(progressInterval)

    // Simulate progress with increasing increments
    progressInterval = setInterval(() => {
      if (routeLoadingProgress.value < 90) {
        // Gradually slow down as we approach 90%
        const increment =
          routeLoadingProgress.value < 30
            ? 10
            : routeLoadingProgress.value < 60
              ? 4
              : routeLoadingProgress.value < 80
                ? 2
                : 1

        routeLoadingProgress.value = Math.min(90, routeLoadingProgress.value + increment)
      }
    }, 100)
  }

  const completeProgress = () => {
    // Clear the interval
    if (progressInterval) {
      clearInterval(progressInterval)
      progressInterval = null
    }

    // Jump to 100% and then hide
    routeLoadingProgress.value = 100

    // After transition completes, reset and hide
    setTimeout(() => {
      isNavigating.value = false
      routeLoadingProgress.value = 0
    }, 300) // Match the CSS transition duration
  }

  // Set up router hooks
  onBeforeMount(() => {
    router.beforeEach((to, from, next) => {
      if (to.path !== from.path) {
        startProgress()
      }
      next()
    })

    router.afterEach(() => {
      completeProgress()
    })

    router.onError(() => {
      completeProgress()
    })
  })

  // Clean up on unmount
  onBeforeUnmount(() => {
    if (progressInterval) {
      clearInterval(progressInterval)
    }
  })

  /**
   * Pre-fetch data for a route to reduce perceived navigation time
   * @param {string} path - The route path
   */
  const prefetchRouteData = (path) => {
    if (!path || !boardId.value) return

    try {
      // Extract route and params from path
      const match = router.resolve(path)
      if (match && match.meta.feature) {
        // Prefetch feature access data
        featureAccessService.checkFeatureAccess({
          boardId: boardId.value,
          feature: match.meta.feature
        })
      }
    } catch (error) {
      logger.warn('Error prefetching route data for route:', path, error.message)
    }
  }

  /**
   * Navigate to a route with improved feedback
   * @param {Object|string} to - Route location object or path
   */
  const navigateTo = async (to) => {
    startProgress()

    try {
      // Prefetch data for the route
      if (typeof to === 'string') {
        prefetchRouteData(to)
      } else if (to.path) {
        prefetchRouteData(to.path)
      }

      // Navigate to the route
      await router.push(to)
    } catch (error) {
      logger.error('Navigation error for route:', JSON.stringify(to), error.message)
      completeProgress()
    }
  }

  return {
    navigationConfig,
    isNavigating,
    routeLoadingProgress,
    prefetchRouteData,
    navigateTo
  }
}
